import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Cookies from 'universal-cookie'
import map from 'lodash/map'
import filter from 'lodash/filter'
import find from 'lodash/find'
import Sidebar from '@SiteContainers/Sidebar'
import Forwarder from '@SiteContainers/Forwarder'
import ProjectsTable from '@SiteContainers/ProjectsTable'
import LanguagesView from '@SiteContainers/LanguagesView'
import ProjectVersionsView from '@SiteContainers/ProjectVersionsView'
import NewProjectPopup from '@SiteContainers/NewProjectPopup'
import SetupProjectPopup from '@SiteContainers/SetupProjectPopup'
import SetupProjectPopupTts from '@SiteContainers/SetupProjectPopupTts'
import ImportSrtPopup from '@SiteContainers/ImportSrtPopup'
import ExternalTokenPopup from '@SiteContainers/ExternalTokenPopup'
import ProjectSharingPopup from '@SiteContainers/ProjectSharingPopup'
import DeleteProjectPopup from '@SiteContainers/DeleteProjectPopup/DeleteProjectPopup'
import RenameProjectPopup from '@SiteContainers/RenameProjectPopup/RenameProjectPopup'
import ExportationPopup from '@SiteContainers/ExportationPopup/ExportationPopup'
import NewsletterPopup from '@SiteContainers/NewsletterPopup'
import LabelsManagerPopup from '@SiteContainers/LabelsManagerPopup'
import MoveToOtherWorkspacePopup from '@SiteContainers/MoveToOtherWorkspacePopup'
import RenewSubscriptionPopup from '@SiteComponents/RenewSubscriptionPopup'

import {
  IOpenSetupProjectPopupPayload,
} from '@SiteModules/Popup/types'

import {
  fetchLabels,
  setLabelSelection,
} from '@SiteModules/Label/actions'

import {
  fetchProjects,
  setListActive,
  setGridActive,
} from '@SiteModules/Project/actions'

import {
  openSetupProjectPopup,
  closeDashboardPopups,
  openSetupProjectPopupTts,
  openRenewSubscriptionPopup,
} from '@SiteModules/Popup/actions'

import {
  getHasLoadedInitially as getHasLoadedProjectsInitially,
  getProjects,
  getListActive,
  getProjectVersions,
  getSearchText,
} from '@SiteModules/Project/selectors'

import {
  getCurrentWorkspaceId,
} from '@SiteModules/Workspace/selectors'

import {
  getNewProjectPopupIsOpen,
  getSetupProjectPopupIsOpen,
  getSetupProjectPopupTtsIsOpen,
  getImportSrtPopupIsOpen,
  getLabelsManagerPopupIsOpen,
  getExternalTokenPopupIsOpen,
  getProjectSharingPopupIsOpen,
  getDeleteProjectPopupIsOpen,
  getDeleteProjectVersionPopupIsOpen,
  getRenameProjectPopupIsOpen,
  getRenameProjectVersionPopupIsOpen,
  getExportationPopupIsOpen,
  getNewsletterPopupIsOpen,
  getMoveToOtherWorkspacePopupIsOpen,
  getRenewSubscriptionPopupIsOpen,
  getEditingProjectIds,
  getEditingSubtitleIds,
} from '@SiteModules/Popup/selectors'

import {
  getLabels,
  getHasLoadedInitially as getHasLoadedLabelsInitially,
} from '@SiteModules/Label/selectors'

import {
  getCurrentAccountHasCurrentSubscription,
  getUserIsAccountAdmin,
} from '@SiteModules/Account/selectors'

import './Dashboard.scss'

const getPreviouslySelectedLabelIds = (): number[] => {
  const selectedLabelIds = new Cookies().get('selectedLabelIds')

  return Array.isArray(selectedLabelIds) ? selectedLabelIds : []
}

const Dashboard: React.FC = () => {
  const dispatch = useDispatch()

  const hasLoadedProjectsInitially = useSelector(getHasLoadedProjectsInitially)
  const hasLoadedLabelsInitially = useSelector(getHasLoadedLabelsInitially)
  const projects = useSelector(getProjects)
  const labels = useSelector(getLabels)
  const currentWorkspaceId = useSelector(getCurrentWorkspaceId)
  const newProjectPopupIsOpen = useSelector(getNewProjectPopupIsOpen)
  const setupProjectPopupIsOpen = useSelector(getSetupProjectPopupIsOpen)
  const setupProjectPopupTtsIsOpen = useSelector(getSetupProjectPopupTtsIsOpen)
  const importSrtPopupIsOpen = useSelector(getImportSrtPopupIsOpen)
  const externalTokenPopupIsOpen = useSelector(getExternalTokenPopupIsOpen)
  const projectSharingPopupIsOpen = useSelector(getProjectSharingPopupIsOpen)
  const deleteProjectPopupIsOpen = useSelector(getDeleteProjectPopupIsOpen)
  const deleteProjectVersionPopupIsOpen = useSelector(getDeleteProjectVersionPopupIsOpen)
  const renameProjectPopupIsOpen = useSelector(getRenameProjectPopupIsOpen)
  const renameProjectVersionPopupIsOpen = useSelector(getRenameProjectVersionPopupIsOpen)
  const exportationPopupIsOpen = useSelector(getExportationPopupIsOpen)
  const labelsManagerPopupIsOpen = useSelector(getLabelsManagerPopupIsOpen)
  const newsletterPopupIsOpen = useSelector(getNewsletterPopupIsOpen)
  const moveToOtherWorkspacePopupIsOpen = useSelector(getMoveToOtherWorkspacePopupIsOpen)
  const renewSubscriptionPopupIsOpen = useSelector(getRenewSubscriptionPopupIsOpen)
  const editingProjectIds = useSelector(getEditingProjectIds)
  const editingSubtitleIds = useSelector(getEditingSubtitleIds)
  const listActive = useSelector(getListActive)
  const searchText = useSelector(getSearchText)
  const currentAccountHasCurrentSubscription = useSelector(getCurrentAccountHasCurrentSubscription)
  const userIsAccountAdmin=useSelector(getUserIsAccountAdmin)
  const allProjectVersions = useSelector(getProjectVersions)

  const [openedProjectId, setOpenedProjectId] = useState('')
  const [openedProjectVersionId, setOpenedProjectVersionId] = useState('')
  const [fullProjectVersionsView, setFullProjectVersionsView] = useState(false)
  const [fullLanguagesView, setFullLanguagesView] = useState(false)

  useEffect(() => {
    const listActiveCookieValue = window.Checksub.cookies.get('listActive')
    const shouldShowGrid = typeof(listActiveCookieValue) === 'boolean' && !listActiveCookieValue
    if (shouldShowGrid) {
      dispatch(setGridActive())
    }
  }, [])

  useEffect(() => {
    if (currentWorkspaceId < 0 || hasLoadedLabelsInitially) { return }

    dispatch(fetchLabels({ workspaceId: currentWorkspaceId }))
  }, [currentWorkspaceId, hasLoadedLabelsInitially])

  useEffect(() => {
    if (currentWorkspaceId < 0 || hasLoadedProjectsInitially || !hasLoadedLabelsInitially) { return }

    const previouslySelectedLabelIds = getPreviouslySelectedLabelIds()
    const shouldRestoreLabelSelection = previouslySelectedLabelIds.length > 0

    if (shouldRestoreLabelSelection && !hasLoadedLabelsInitially) { return }

    resetOpenedProject()
    resetOpenedProjectVersion()

    const selectedLabels = shouldRestoreLabelSelection
      ? getValidSelectedLabelIds(previouslySelectedLabelIds)
      : []

    if (shouldRestoreLabelSelection) {
      dispatch(setLabelSelection({ labelIds: selectedLabels, suppressProjectsFetch: true }))
    }

    dispatch(fetchProjects(buildFetchProjectsPayload(selectedLabels)))
  }, [currentWorkspaceId, hasLoadedProjectsInitially, hasLoadedLabelsInitially, labels, searchText])

  useEffect(() => {
    if (currentWorkspaceId < 0) { return }

    const url = new URL(window.location.href)
    const shouldOpenSetupProjectPopupForAddingLanguages = url.searchParams.get('addLanguages') === 'true'
    const shouldOpenSetupProjectPopupForAddingClips = url.searchParams.get('addClips') === 'true'
    const shouldOpenSetupProjectPopupTts = url.searchParams.get('addLanguagesTts') === 'true'

    if (!shouldOpenSetupProjectPopupForAddingLanguages && !shouldOpenSetupProjectPopupForAddingClips && !shouldOpenSetupProjectPopupTts) {
      return
    }

    const projectVersionId = url.searchParams.get('projectVersionId') as string

    if (shouldOpenSetupProjectPopupForAddingLanguages || shouldOpenSetupProjectPopupForAddingClips) {
      if (currentAccountHasCurrentSubscription) {
        const openSetupProjectPopupPayload: IOpenSetupProjectPopupPayload = {
          projectVersionIds: [projectVersionId],
          ...(shouldOpenSetupProjectPopupForAddingClips ? { projectType: 'repurpose' } : {})
        }
        dispatch(openSetupProjectPopup(openSetupProjectPopupPayload))
      } else {
        dispatch(openRenewSubscriptionPopup())
      }
    }

    if (shouldOpenSetupProjectPopupTts) {
      if (currentAccountHasCurrentSubscription) {
        dispatch(openSetupProjectPopupTts({ projectVersionIds: [projectVersionId] }))
      } else {
        dispatch(openRenewSubscriptionPopup())
      }
    }

    url.searchParams.delete('addLanguages')
    url.searchParams.delete('addClips')
    url.searchParams.delete('addLanguagesTts')
    url.searchParams.delete('projectVersionId')
    window.history.replaceState({}, '', url)
  }, [currentWorkspaceId, currentAccountHasCurrentSubscription])


  const getValidSelectedLabelIds = (previouslySelectedLabelIds: number[]) => {
    const labelIds = map(labels, label => label.id)
    return filter(previouslySelectedLabelIds, labelId => labelIds.includes(labelId))
  }

  const buildFetchProjectsPayload = (selectedLabels: number[]) => {
    return {
      workspaceId: currentWorkspaceId,
      page: 1,
      selectedLabels,
      ...(searchText.length > 0 ? { searchText } : {})
    }
  }

  const handleCloseDashboardPopups = () => {
    dispatch(closeDashboardPopups())
  }

  const resetOpenedProject = () => {
    setOpenedProjectId('')
    setFullProjectVersionsView(false)
  }

  const resetOpenedProjectVersion = () => {
    setOpenedProjectVersionId('')
    setFullLanguagesView(false)
  }

  const handleSetListActive = (newListActive: boolean) => {
    if (newListActive) {
      dispatch(setListActive())
    } else {
      dispatch(setGridActive())
    }
  }

  const shouldRenderLanguagesView = openedProjectVersionId.length > 0
  const shouldRenderProjectVersionsView = openedProjectId.length > 0
  const showDashboardWithFullLanguagesView = shouldRenderLanguagesView && fullLanguagesView
  const showDashboardWithFullProjectVersionsView = shouldRenderProjectVersionsView && fullProjectVersionsView

  const renderDashboard = () => {
    return (
      <div
        className="Dashboard"
        id="dashboard"
      >
        <Sidebar
          pageName="projects"
        />
        <ProjectsTable
          showDashboardWithFullLanguagesOrProjectVersionsView={showDashboardWithFullLanguagesView || showDashboardWithFullProjectVersionsView}
          setOpenedProjectId={setOpenedProjectId}
          setOpenedProjectVersionId={setOpenedProjectVersionId}
          listActive={listActive}
          setListActive={handleSetListActive}
        />
        {renderPopups()}
        {renderProjectVersionsView()}
        {renderLanguagesView()}
      </div>
    )
  }

  const renderPopups = () => {
    return (
      <React.Fragment>
        {newProjectPopupIsOpen && renderNewProjectPopup()}
        {setupProjectPopupIsOpen && renderSetupProjectPopup()}
        {setupProjectPopupTtsIsOpen && renderSetupProjectPopupTts()}
        {importSrtPopupIsOpen && renderSrtImportPopup()}
        {externalTokenPopupIsOpen && renderExternalTokenPopup()}
        {projectSharingPopupIsOpen && renderProjectSharingPopup()}
        {deleteProjectPopupIsOpen && renderDeleteProjectPopup(false)}
        {deleteProjectVersionPopupIsOpen && renderDeleteProjectPopup(true)}
        {renameProjectPopupIsOpen && renderRenameProjectPopup(false)}
        {renameProjectVersionPopupIsOpen && renderRenameProjectPopup(true)}
        {exportationPopupIsOpen && renderExportationPopup()}
        {labelsManagerPopupIsOpen && renderLabelsManagerPopup()}
        {newsletterPopupIsOpen && renderNewsletterPopup()}
        {moveToOtherWorkspacePopupIsOpen && renderMoveToOtherWorkspacePopup()}
        {renewSubscriptionPopupIsOpen && renderRenewSubscriptionPopup()}
      </React.Fragment>
    )
  }

  const renderNewProjectPopup = () => {
    return <NewProjectPopup />
  }

  const renderSetupProjectPopup = () => {
    return <SetupProjectPopup />
  }

  const renderSetupProjectPopupTts = () => {
    return <SetupProjectPopupTts />
  }

  const renderExternalTokenPopup = () => {
    return (
      <ExternalTokenPopup
        closePopup={handleCloseDashboardPopups}
      />
    )
  }

  const renderProjectSharingPopup = () => {
    return (
      <ProjectSharingPopup
        projectIds={editingProjectIds}
        subtitleId={editingSubtitleIds[0]}
        includePublicLink={false}
        closePopup={handleCloseDashboardPopups}
      />
    )
  }

  const renderSrtImportPopup = () => {
    return (
      <ImportSrtPopup
        closePopup={handleCloseDashboardPopups}
      />
    )
  }

  const renderDeleteProjectPopup = (isForProjectVersions: boolean) => {
    return (
      <DeleteProjectPopup
        isForProjectVersions={isForProjectVersions}
      />
    )
  }

  const renderRenameProjectPopup = (isForProjectVersions: boolean) => {
    return (
      <RenameProjectPopup
        isForProjectVersions={isForProjectVersions}
      />
    )
  }

  const renderExportationPopup = () => {
    return <ExportationPopup />
  }

  const renderLabelsManagerPopup = () => {
    return <LabelsManagerPopup />
  }

  const renderNewsletterPopup = () => {
    return <NewsletterPopup />
  }

  const renderMoveToOtherWorkspacePopup = () => {
    return <MoveToOtherWorkspacePopup />
  }

  const renderRenewSubscriptionPopup = () => {
    return (
      <RenewSubscriptionPopup
        tooltipDelay={window.Checksub.tooltipDelay}
        userCanManageSubscription={userIsAccountAdmin}
        handleClose={handleCloseDashboardPopups}
      />
    )
  }

  const renderLanguagesView = () => {
    const openedProjectVersion = find(allProjectVersions, projectVersion => projectVersion.id === openedProjectVersionId)
    const openedProject = openedProjectVersion ? find(projects, project => project.id === openedProjectVersion.attributes.projectId) : undefined
    const originalProjectVersionOfOpenedProject = openedProject ? find(openedProject.projectVersions, projectVersion => !projectVersion.attributes.isRepurposed) : undefined

    return (
      <LanguagesView
        shouldRender={shouldRenderLanguagesView}
        projectVersion={openedProjectVersion ? openedProjectVersion : originalProjectVersionOfOpenedProject}
        project={openedProject}
        resetOpenedProjectVersion={resetOpenedProjectVersion}
        fullLanguagesView={fullLanguagesView}
        setFullLanguagesView={setFullLanguagesView}
        setOpenedProjectVersionId={setOpenedProjectVersionId}
      />
    )
  }

  const renderProjectVersionsView = () => {
    const openedProject = find(projects, project => project.id === openedProjectId)

    return (
      <ProjectVersionsView
        shouldRender={shouldRenderProjectVersionsView}
        project={openedProject}
        resetOpenedProject={resetOpenedProject}
        fullProjectVersionsView={fullProjectVersionsView}
        setFullProjectVersionsView={setFullProjectVersionsView}
        setOpenedProjectId={setOpenedProjectId}
        setOpenedProjectVersionId={setOpenedProjectVersionId}
      />
    )
  }

  return (
    <React.Fragment>
      <Forwarder />
      {renderDashboard()}
    </React.Fragment>
  )
}

export default Dashboard
